import { Injectable } from "@angular/core";
import { take } from "rxjs/operators";
import { ApiService } from "../api.service";
import { FrontendLoggerService } from "../frontend-logger.service";
import { Theme, ThemeService } from "./theme.service";

/**
 * Une la preferencia de tema local (ThemeService) con la persistida por usuario
 * en el backend, de modo que la eleccion viaje entre navegadores y dispositivos.
 *
 * ThemeService sigue siendo el unico dueno del DOM y de localStorage; este
 * servicio solo anade el viaje de ida (persistir) y vuelta (aplicar el perfil).
 */
@Injectable({
  providedIn: "root",
})
export class UserThemePreferenceService {
  private static readonly ENDPOINT = "user/preferences/theme";

  constructor(
    private readonly apiService: ApiService,
    private readonly themeService: ThemeService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  /** Alterna el tema y persiste el resultado para el usuario autenticado. */
  toggle(): void {
    this.themeService.toggleTheme();
    this.persist(this.themeService.getCurrentTheme());
  }

  /** Fija un tema concreto y lo persiste. */
  select(theme: Theme): void {
    this.themeService.setTheme(theme);
    this.persist(theme);
  }

  /**
   * Aplica la preferencia que llega en el perfil del usuario. No la re-persiste:
   * es la fuente de verdad remota, y reenviarla provocaria una escritura inutil.
   * Los valores desconocidos o ausentes se ignoran y dejan el tema local intacto.
   */
  applyFromProfile(theme: unknown): void {
    if (theme !== "light" && theme !== "dark") {
      return;
    }
    if (theme === this.themeService.getCurrentTheme()) {
      return;
    }
    this.themeService.setTheme(theme);
  }

  /**
   * El fallo de red no revierte el cambio local: la preferencia ya quedo en
   * localStorage y revertirla seria mas confuso para el usuario que perderla.
   */
  private persist(theme: Theme): void {
    this.apiService
      .put<{ theme: Theme }>(UserThemePreferenceService.ENDPOINT, { theme })
      .pipe(take(1))
      .subscribe({
        error: (error) =>
          this.frontendLogger.warn(
            `No se pudo guardar la preferencia de tema: ${
              (error as Error)?.message || String(error)
            }`,
            "UserThemePreferenceService.persist"
          ),
      });
  }
}
